import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../../config/api";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import { GovernanceListingSection } from "../../components/governance/GovernanceListingSection";
import { ComplianceCenterMetricTile } from "../../components/compliance-center/ComplianceCenterMetricTile";
import listStyles from "@/styles/pages/governance/GovernanceListing.module.scss";

export default function ComplianceCenterTrustWorkspace() {
  const { t } = useI18n();
  const { token } = useAuth();
  const [packagesCount, setPackagesCount] = useState(0);
  const [attestationsCount, setAttestationsCount] = useState(0);
  const [controlsCount, setControlsCount] = useState(0);
  const [warning, setWarning] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setWarning(t("complianceCenterUi.trustUnavailable", "Trust Center metrics are temporarily unavailable."));
      return;
    }

    const load = async () => {
      setLoading(true);
      setWarning(null);
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 15000);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const summaryRes = await fetch(API_ENDPOINTS.CUSTOMER_TRUST_SUMMARY, {
          headers,
          signal: controller.signal,
        });
        if (!summaryRes.ok) {
          setWarning(t("complianceCenterUi.trustUnavailable", "Trust Center metrics are temporarily unavailable."));
          setPackagesCount(0);
          setAttestationsCount(0);
          setControlsCount(0);
          return;
        }
        const summaryJson = await summaryRes.json().catch(() => ({}));
        const totalRecords = Number(summaryJson?.data?.totalRecords || 0);
        const categoryDistribution = summaryJson?.data?.categoryDistribution || {};
        const controlsFromCategories = Object.values(categoryDistribution).reduce<number>(
          (sum, value) => sum + Number(value || 0),
          0,
        );
        setPackagesCount(totalRecords);
        setAttestationsCount(totalRecords);
        setControlsCount(Number(controlsFromCategories || totalRecords));
      } catch {
        setWarning(t("complianceCenterUi.trustUnavailable", "Trust Center metrics are temporarily unavailable."));
        setPackagesCount(0);
        setAttestationsCount(0);
        setControlsCount(0);
      } finally {
        window.clearTimeout(timeoutId);
        setLoading(false);
      }
    };
    void load();
  }, [token, t]);

  return (
    <div className={listStyles.pageStack}>
      <GovernanceListingSection
        title={t("complianceCenterUi.nav.trustCenter", "Trust Center")}
        description={t(
          "complianceCenterUi.trustIntro",
          "Assurance packages, attestations, and public control disclosures.",
        )}
      >
        {warning ? (
          <p className={listStyles.formError} role="status">
            {warning}
          </p>
        ) : null}
        {loading ? (
          <p className={listStyles.panelSubtitle}>{t("common.loading", "Loading…")}</p>
        ) : (
          <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
            <ComplianceCenterMetricTile
              label={t("complianceCenterUi.assurancePackages", "Assurance packages")}
              value={packagesCount}
              tone="blue"
            />
            <ComplianceCenterMetricTile label={t("complianceCenterUi.attestations", "Attestations")} value={attestationsCount} tone="green" />
            <ComplianceCenterMetricTile label={t("complianceCenterUi.publicControls", "Public controls")} value={controlsCount} tone="purple" />
          </div>
        )}
      </GovernanceListingSection>
    </div>
  );
}
